import { Controller } from "@hotwired/stimulus";
import mapboxgl from "mapbox-gl";

const MAIN_COLORS = [
  "#46da9c",
  "#38ae7c",
  "#6ae1af",
  "#2a825d",
  "#90e8c3",
  "#3fc48c",
  "#58dda5",
  "#31986d",
  "#7de5b9",
  "#236d4e",
  "#a2eccd",
  "#1c573e",
  "#b5f0d7",
  "#0e2b1f",
  "#daf7eb",
  "#15412e",
  "#c7f3e1",
  "#07150f",
  "#ecfbf5",
];

const HOVER_COLORS = [
  "#339e71",
  "#287e5a",
  "#5bb18d",
  "#1e5e43",
  "#84c4a9",
  "#2d8e65",
  "#47a77f",
  "#236e4f",
  "#70bb9b",
  "#194f38",
  "#99ceb8",
  "#143f2d",
  "#add8c6",
  "#0a1f16",
  "#d6ebe2",
  "#0f2f21",
  "#c1e1d4",
  "#050f0b",
  "#eaf5f0",
];

// Connects to data-controller="coverage-map"
export default class extends Controller {
  static values = {
    // Array of objects of this type
    // {
    //   name: String,
    //   zip_codes: Array<string>,
    //   popup: string (html),
    //   marker: {
    //     lat: float,
    //     lng: float,
    //     image_url: string (if in assets),
    //     icon: string (html) (if no image_url)
    //   }
    // }
    areas: Array,
    colorful: Boolean, // if true, each area is a different shade of green
  };

  connect() {
    const map = this.#initMap();

    this.#showAreas(map);

    this.resizeObserver = new ResizeObserver(() => {
      map.resize();
    });
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.resizeObserver.disconnect();
  }

  #initMap() {
    mapboxgl.accessToken = process.env.MAPBOX_API_KEY;

    const map = new mapboxgl.Map({
      container: this.element,
      style: "mapbox://styles/mapbox/light-v11",
      cooperativeGestures: true,
    });

    const markers = this.areasValue.map(({ marker }) => marker);

    const bounds = new mapboxgl.LngLatBounds();
    if (markers.length) {
      markers.forEach((marker) => bounds.extend([marker.lng, marker.lat]));
    } else {
      bounds.extend([6.7759719, 46.5704405]); // Coworking du Jorat, le centre du monde
    }
    map.fitBounds(bounds, { maxZoom: 8 });

    // Add zoom and rotation controls to the map.
    map.addControl(new mapboxgl.NavigationControl());

    return map;
  }

  #showAreas(map) {
    map.on("load", () => {
      map.addSource("ch-plz", {
        type: "vector",
        url: "mapbox://tanguy-rdf.6qo8otpa",
        promoteId: "mapbox_id",
      });

      this.areasValue.forEach(({ name, zip_codes, popup, marker }, index) => {
        const mainColor = this.colorfulValue
          ? MAIN_COLORS[index % MAIN_COLORS.length]
          : MAIN_COLORS[0];
        const hoverColor = this.colorfulValue
          ? HOVER_COLORS[index % HOVER_COLORS.length]
          : HOVER_COLORS[0];

        map.addLayer({
          id: name,
          type: "fill",
          source: "ch-plz",
          "source-layer": "chplzfull",
          filter: ["in", "plz"].concat(zip_codes),
          paint: {
            "fill-color": mainColor,
            "fill-opacity": 0.8,
          },
        });

        this.#addMarker(map, marker);

        map.on("click", () => {
          map.setPaintProperty(name, "fill-color", mainColor);
        });

        map.on("click", name, ({ lngLat }) => {
          map.setPaintProperty(name, "fill-color", hoverColor);

          if (popup) {
            new mapboxgl.Popup().setLngLat(lngLat).setHTML(popup).addTo(map);
          }
        });
      });
    });
  }

  #addMarker(map, marker) {
    if (!marker) return;

    const element = document.createElement("div");

    if (marker.icon) {
      // If given `icon` html, make sure it fits well in a 30px x 30px
      element.innerHTML = marker.icon;
      element.style.width = "30px";
      element.style.height = "30px";
    } else if (marker.image_url) {
      element.style.backgroundImage = `url('${marker.image_url}')`;
      element.style.backgroundSize = "contain";
      element.style.width = "27px";
      element.style.height = "30px";
    }
    const mapMarker = new mapboxgl.Marker(element).setLngLat([
      marker.lng,
      marker.lat,
    ]);

    mapMarker.addTo(map);
  }
}
